import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useForm } from "react-hook-form";
import ModalCloseButton from "./ModalCloseButton";
import HeadService from "../services/HeadService";
import { showErrorToast } from "../utils/AlertUtils";
import guLocale from "../locales/gu";

const LedgerModal = ({ show, handleClose, handleSubmit, initialData }) => {
  const locale = guLocale.ledgerModal;
  const {
    register,
    handleSubmit: hookFormHandleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      head_id: "",
      details: "",
      amount: "",
      transaction_date: "",
      transaction_type: "IN",
      payment_method: "cash",
      cheque_number: "",
      cheque_pfms_clearing_date: "",
    },
  });

  const watchPaymentMethod = watch("payment_method");
  const [heads, setHeads] = useState([]);
  const transactionTypes = [
    // Static transaction types
    { value: "IN", label: locale.transactionTypeIn },
    { value: "OUT", label: locale.transactionTypeOut },
  ];

  const fetchHeads = async () => {
    try {
      const response = await HeadService.getHeads();
      setHeads(response.data);
    } catch (error) {
      console.error("Error fetching heads in LedgerModal", error);
      showErrorToast(error.message);
    }
  };

  useEffect(() => {
    if (show) {
      if (heads.length === 0) { // Only fetch if heads not loaded
        fetchHeads();
      }
      if (initialData) {
        reset({
          head_id: initialData?.head_id?._id || "",
          details: initialData?.details || "",
          amount: initialData?.amount || "",
          transaction_date: initialData?.transaction_date
            ? initialData?.transaction_date.slice(0, 10)
            : "",
          transaction_type: initialData?.transaction_type || "IN",
          payment_method: initialData?.payment_method || "cash",
          cheque_number: initialData?.cheque_number || "",
          cheque_pfms_clearing_date: initialData?.cheque_pfms_clearing_date
            ? initialData?.cheque_pfms_clearing_date.slice(0, 10)
            : "",
        });
      } else {
        reset({
          head_id: "",
          details: "",
          amount: "",
          transaction_date: "",
          transaction_type: "IN",
          payment_method: "cash",
          cheque_number: "",
          cheque_pfms_clearing_date: "",
        });
      }
    }
  }, [show, initialData, reset, heads.length]);

  const onFormSubmit = async (data) => {
    try {
      await handleSubmit(data);
      handleClose();
    } catch (error) {
      console.error("Error submitting ledger form", error);
      showErrorToast(error.message);
    }
  };

  const handleModalClose = () => {
    reset();
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose} centered size="lg">
      <Modal.Header>
        <Modal.Title>{initialData ? locale.editLedgerModalTitle : locale.addLedgerModalTitle }</Modal.Title>
        <ModalCloseButton onClick={handleModalClose} />
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={hookFormHandleSubmit(onFormSubmit)}>
          <Form.Group controlId="ledger_head" className="mb-2">
            <Form.Label>{locale.headLabel}</Form.Label>
            <Form.Control
              as="select"
              {...register("head_id", { required: locale.headRequired })}
              isInvalid={!!errors.head_id}
            >
              <option value="">{locale.selectHead}</option>
              {heads.map((head) => (
                <option key={head._id} value={head._id}>
                  {head.head_name}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.head_id && errors.head_id.message}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="ledger_details" className="mb-2">
            <Form.Label>{locale.descriptionLabel}</Form.Label>
            <Form.Control
              as="textarea"
              placeholder={locale.enterDescriptionPlaceholder}
              {...register("details", { required: locale.detailsRequired })}
              isInvalid={!!errors.details}
            />
            <Form.Control.Feedback type="invalid">
              {errors.details && errors.details.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Row className="mb-2">
            <Col>
              <Form.Group controlId="ledger_amount">
                <Form.Label>{locale.amountLabel}</Form.Label>
                <Form.Control
                  type="number"
                  placeholder={locale.enterAmountPlaceholder}
                  {...register("amount", {
                    required: locale.amountRequired,
                    valueAsNumber: true,
                  })}
                  isInvalid={!!errors.amount}
                  readOnly={!!initialData && initialData?.transaction_type === 'OUT' && initialData?.payment_method === 'cash'}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.amount && errors.amount.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="ledger_transaction_date">
                <Form.Label>{locale.transactionDateLabel}</Form.Label>
                <Form.Control
                  type="date"
                  placeholder="Select date"
                  {...register("transaction_date", {
                    required: locale.transactionDateRequired,
                  })}
                  isInvalid={!!errors.transaction_date}
                  readOnly={!!initialData && initialData?.transaction_type === 'OUT' && initialData?.payment_method === 'cash'}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.transaction_date && errors.transaction_date.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <Form.Group controlId="transaction_type" className="mb-3">
                <Form.Label>આવક/જાવક</Form.Label>
                <Form.Control
                  as="select"
                  {...register("transaction_type", {
                    required: locale.transactionTypeRequired,
                  })}
                  isInvalid={!!errors.transaction_type}
                  readOnly={!!initialData && initialData?.transaction_type === 'OUT' && initialData?.payment_method === 'cash'}
                  disabled={!!initialData && initialData?.transaction_type === 'OUT' && initialData?.payment_method === 'cash'}
                >
                  <option value="">{locale.selectTransactionType}</option>
                  {transactionTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {errors.transaction_type && errors.transaction_type.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="ledger_payment_method">
                <Form.Label>આવક/ચુકવણી પદ્ધતિ</Form.Label>
                <Form.Control
                  as="select"
                  {...register("payment_method", {
                    required: locale.paymentMethodRequired,
                  })}
                  isInvalid={!!errors.payment_method}
                  readOnly={!!initialData && initialData?.transaction_type === 'OUT' && initialData?.payment_method === 'cash'}
                  disabled={!!initialData && initialData?.transaction_type === 'OUT' && initialData?.payment_method === 'cash'}
                >
                  <option value="cash">{locale.paymentMethodCash}</option>
                  <option value="cheque">{locale.paymentMethodCheque}</option>
                  <option value="online">{locale.paymentMethodOnline}</option>{" "}
                  {/* Added Online option */}
                  {/* Add more options as needed */}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {errors.payment_method && errors.payment_method.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {watchPaymentMethod === "cheque" && (
            <>
              <Row className="mb-2">
                <Col>
                  <Form.Group controlId="ledger_cheque_number">
                    <Form.Label>{locale.chequeNumberLabel}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={locale.enterChequeNumberPlaceholder}
                      {...register("cheque_number", {
                        required: locale.chequeNumberRequired,
                      })}
                      isInvalid={!!errors.cheque_number}
                      readOnly={!!initialData && initialData?.transaction_type === 'OUT' && initialData?.payment_method === 'cash'}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.cheque_number && errors.cheque_number.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group controlId="ledger_cheque_pfms_date">
                    <Form.Label>{locale.chequePFMSClearingDateLabel}</Form.Label>
                    <Form.Control
                      type="date"
                      {...register("cheque_pfms_clearing_date", {
                        required: locale.chequePFMSClearingDateRequired,
                      })}
                      isInvalid={!!errors.cheque_pfms_clearing_date}
                      readOnly={!!initialData && initialData?.transaction_type === 'OUT' && initialData?.payment_method === 'cash'}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.cheque_pfms_clearing_date &&
                        errors.cheque_pfms_clearing_date.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose}>
          {locale.cancelButtonText}
        </Button>
        <Button
          variant="primary"
          type="submit"
          onClick={hookFormHandleSubmit(onFormSubmit)}
        >
          {locale.submitButtonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LedgerModal;
